import React, { useRef } from 'react'
import CreatorAccordion from '../../../ui_reusable/CreatorAccordion'
import DatasetForm from './dataset_form/DatasetForm'
import {
  getDatasetsListQueryKey,
  useDatasetsCreate,
} from '../../../../api/generated/endpoints/datasets/datasets'
import type { DatasetCreateRequest } from '../../../../api/generated/models'
import { validateAndLog } from '../../../../utils/zodDebugger'
import { datasetsRetrieveResponse } from '../../../../api/generated/validation/datasets/datasets.zod'
import { useQueryClient } from '@tanstack/react-query'
import { useDatasetsRegionDatasetsCreate } from '../../../../api/generated/endpoints/region-datasets/region-datasets'
import {
  transformToDatasetRequest,
  transformToRegionDatasetRequest,
  extractRegionId,
  validateTransformedData,
} from './dataset_form/dataset_form/transformations'
import type { FormSubmitResult, FileInfo } from './types/DatasetHandlingTypes'

/**
 * CREATEDATASET COMPONENT
 *
 * Wrapper component that handles API mutations for dataset creation.
 * Routes to different API endpoints based on whether creating a regular dataset or segment.
 *
 * RESPONSIBILITIES:
 * 1. Provide mutation hooks for datasets and regional datasets
 * 2. Transform form data to API format using transformation utilities
 * 3. Route to correct API endpoint based on entity type
 * 4. Invalidate queries after successful creation
 * 5. Handle API errors and return to form
 */

interface CreateDatasetProps {}

const CreateDataset: React.FC<CreateDatasetProps> = () => {
  const panelRef = useRef<any>(null)
  const queryClient = useQueryClient()

  // MUTATION HOOKS

  /**
   * Mutation for creating regular datasets (contextual, client, media channel)
   */
  const createDatasetMutation = useDatasetsCreate({
    mutation: {
      onSuccess: () => {
        // Invalidate driver list queries to refresh the databook
        queryClient.invalidateQueries({
          queryKey: ['/api/v2/datasets/drivers/with-channels'],
        })
        queryClient.invalidateQueries({
          queryKey: ['/api/v2/datasets/drivers/without-channels'],
        })
        queryClient.invalidateQueries({
          queryKey: ['/api/v2/datasets/drivers/'],
        })
        queryClient.invalidateQueries({
          queryKey: getDatasetsListQueryKey(),
        })
      },
      onError: (error) => {
        console.error('Failed to create dataset:', error)
      },
    },
  })

  /**
   * Mutation for creating regional datasets (segments)
   */
  const createRegionDatasetMutation = useDatasetsRegionDatasetsCreate({
    mutation: {
      onSuccess: () => {
        // Invalidate driver list queries to refresh the databook
        queryClient.invalidateQueries({
          queryKey: ['/api/v2/datasets/drivers/with-channels'],
        })
        queryClient.invalidateQueries({
          queryKey: ['/api/v2/datasets/drivers/without-channels'],
        })
        queryClient.invalidateQueries({
          queryKey: ['/api/v2/datasets/drivers/'],
        })
        // Invalidate region datasets list
        queryClient.invalidateQueries({
          queryKey: ['/api/datasets/region-datasets/'],
        })
      },
      onError: (error) => {
        console.error('Failed to create region dataset:', error)
      },
    },
  })

  // SUBMIT HANDLER

  /**
   * Handles form submission by routing to the appropriate API endpoint
   *
   * ROUTING LOGIC:
   * - If formData.isSegment === true → Create RegionalDataset
   * - Otherwise → Create Dataset
   *
   * @param formData - Partial dataset data from form
   * @param fileInfo - File upload info (can be null)
   * @returns Success/error result
   */
  const handleSubmit = async (
    formData: Partial<DatasetCreateRequest>,
    fileInfo: FileInfo | null,
  ): Promise<FormSubmitResult> => {
    try {
      const isSegment = (formData as any).isSegment === true

      // ========================================================================
      // SEGMENT CREATION (Regional Dataset)
      // ========================================================================
      if (isSegment) {
        // Extract parent dataset ID from form data
        const parentDatasetId = extractRegionId(
          (formData as any).selectedParentDataset || [],
        )

        if (!parentDatasetId) {
          return {
            success: false,
            error: 'Parent driver is required',
          }
        }

        // Extract region ID from form data
        const regionId = extractRegionId(
          (formData as any).selectedRegions || [],
        )

        if (!regionId) {
          return {
            success: false,
            error: 'Segment name is required',
          }
        }

        // Transform to region dataset format
        const segmentPayload = await transformToRegionDatasetRequest(
          parentDatasetId,
          regionId,
          fileInfo,
        )

        // Validate transformed data
        const validationError = validateTransformedData(segmentPayload, true)
        if (validationError) {
          return {
            success: false,
            error: validationError,
          }
        }

        // Submit to region dataset API
        await createRegionDatasetMutation.mutateAsync({
          data: segmentPayload as any,
        })

        return { success: true }
      }

      // DATASET CREATION (Contextual, Client, or Media Channel)

      // Transform form data to API format
      let requestData = await transformToDatasetRequest(formData, fileInfo)

      // Validate transformed data
      const validationError = validateTransformedData(requestData, false)
      if (validationError) {
        return {
          success: false,
          error: validationError,
        }
      }

      // Submit to dataset API
      const response = await createDatasetMutation.mutateAsync({
        data: requestData as any,
      })

      // Validate response data (optional - for debugging)
      const validatedResponse = validateAndLog(
        datasetsRetrieveResponse,
        response,
        'CreateDatasetResponse',
      )

      if (!validatedResponse) {
        console.warn(
          'CreateDataset: API response validation failed, but operation may have succeeded',
        )
      }

      return { success: true }
    } catch (error: any) {
      console.error('Create failed:', error)

      // Extract error message for display
      let errorMessage = 'Create failed'

      if (error.response?.status === 500) {
        errorMessage = 'Server error (500) - Check backend logs for details'
      } else if (error.response?.data?.detail) {
        errorMessage = String(error.response.data.detail)
      } else if (error.response?.data?.message) {
        errorMessage = JSON.stringify(error.response.data.message)
      } else if (error.response?.data) {
        // Pass through the full error object for field-level error mapping
        return {
          success: false,
          error: error.response.data,
        }
      } else if (error.message) {
        errorMessage = String(error.message)
      }

      return {
        success: false,
        error: errorMessage,
      }
    }
  }

  // SUCCESS HANDLER

  /**
   * Called after successful creation
   * Collapses the accordion panel for smooth UX
   */
  const handleSuccess = () => {
    if (panelRef.current) {
      panelRef.current.tryCollapse()
    }
  }

  // RENDER

  return (
    <CreatorAccordion id={'createDatasetAccordion'}>
      <CreatorAccordion.Panel ref={panelRef} header="Add new dataset">
        <DatasetForm
          onSubmit={handleSubmit}
          submitButtonText="Save dataset"
          successMessage="Dataset created successfully!"
          onSuccess={handleSuccess}
          resetOnSuccess={true}
        />
      </CreatorAccordion.Panel>
    </CreatorAccordion>
  )
}

export default CreateDataset
